import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, Trash2, ClipboardPaste, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { getWarrantyError, hasWarrantyErrors } from '@/utils/validateWarranty';
import { NumericInput } from '@/components/NumericInput';

const SimpleWindowTypeForm = ({ formData, onChange, onSubmit, isEditing, disabled, onPictureUpload, pictureUrl, onPictureDelete, onPasteFromClipboard, pendingPicture, onRemovePendingPicture, isUploading }) => {
  const [windowOperationTypes, setWindowOperationTypes] = useState([]);
  const [loadingOperationTypes, setLoadingOperationTypes] = useState(false);

  useEffect(() => {
    loadWindowOperationTypes();
  }, []);

  const loadWindowOperationTypes = async () => {
    setLoadingOperationTypes(true);
    try {
      const response = await axios.get(`${API}/window-operation-types`);
      setWindowOperationTypes(response.data || []);
    } catch (error) {
      console.error('Error loading Window Operation Types:', error);
    } finally {
      setLoadingOperationTypes(false);
    }
  };

  const handleInputChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-lg">Basic Information</h3>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Marker Color</Label>
            <input
              type="color"
              value={formData.markerColor || '#39FF14'}
              onChange={(e) => handleInputChange('markerColor', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border border-gray-300"
              title="Choose marker color for elevations"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Window Type *</Label>
          <Input
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., WT1, WT2, Main Entry Window"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Manufacturer *</Label>
            <Input
              value={formData.manufacturer}
              onChange={(e) => handleInputChange('manufacturer', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Model *</Label>
            <Input
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Frame Material *</Label>
            <Input
              value={formData.frameMaterial}
              onChange={(e) => handleInputChange('frameMaterial', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Operation Type *</Label>
            {loadingOperationTypes ? (
              <Input value="Loading..." disabled />
            ) : (
              <Select
                value={formData.operationType || ''}
                onValueChange={(value) => handleInputChange('operationType', value)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select operation type" />
                </SelectTrigger>
                <SelectContent>
                  {windowOperationTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Thermal Performance */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg border-b pb-2">Thermal Performance</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>U Value * <span className="text-xs text-slate-500 font-normal">(Btu/hr·ft²·°F)</span></Label>
            <NumericInput
              value={formData.uValue || ''}
              onChange={(v) => handleInputChange('uValue', v)}
              placeholder="e.g., 0.30"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>CR</Label>
            <NumericInput
              value={formData.cr || ''}
              onChange={(v) => handleInputChange('cr', v)}
              placeholder="e.g., 45"
            />
          </div>
        </div>
      </div>

      {/* Finishes & Hardware */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg border-b pb-2">Finishes & Hardware</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Finish Interior *</Label>
            <Input
              value={formData.finishInterior}
              onChange={(e) => handleInputChange('finishInterior', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Finish Exterior *</Label>
            <Input
              value={formData.finishExterior}
              onChange={(e) => handleInputChange('finishExterior', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Hardware *</Label>
            <textarea
              value={formData.hardware}
              onChange={(e) => handleInputChange('hardware', e.target.value)}
              required
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            />
          </div>
        </div>
      </div>

      {/* Warranties */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg border-b pb-2">Warranties (Years)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Frame Warranty</Label>
            <NumericInput
              value={formData.frameWarrantyYears || ''}
              onChange={(v) => handleInputChange('frameWarrantyYears', v ? parseInt(v) : null)}
              placeholder="e.g., 10"
              className={getWarrantyError(formData.frameWarrantyYears) ? 'border-red-500' : ''}
              data-testid="window-frame-warranty-input"
            />
            {getWarrantyError(formData.frameWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(formData.frameWarrantyYears)}</p>}
          </div>
          <div className="space-y-2">
            <Label>Finish Warranty</Label>
            <NumericInput
              value={formData.finishWarrantyYears || ''}
              onChange={(v) => handleInputChange('finishWarrantyYears', v ? parseInt(v) : null)}
              placeholder="e.g., 5"
              className={getWarrantyError(formData.finishWarrantyYears) ? 'border-red-500' : ''}
              data-testid="window-finish-warranty-input"
            />
            {getWarrantyError(formData.finishWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(formData.finishWarrantyYears)}</p>}
          </div>
          <div className="space-y-2">
            <Label>Hardware Warranty</Label>
            <NumericInput
              value={formData.hardwareWarrantyYears || ''}
              onChange={(v) => handleInputChange('hardwareWarrantyYears', v ? parseInt(v) : null)}
              placeholder="e.g., 10"
              className={getWarrantyError(formData.hardwareWarrantyYears) ? 'border-red-500' : ''}
              data-testid="window-hardware-warranty-input"
            />
            {getWarrantyError(formData.hardwareWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(formData.hardwareWarrantyYears)}</p>}
          </div>
          <div className="space-y-2">
            <Label>Installation Warranty</Label>
            <NumericInput
              value={formData.installationWarrantyYears || ''}
              onChange={(v) => handleInputChange('installationWarrantyYears', v ? parseInt(v) : null)}
              placeholder="e.g., 2"
              className={getWarrantyError(formData.installationWarrantyYears) ? 'border-red-500' : ''}
              data-testid="window-installation-warranty-input"
            />
            {getWarrantyError(formData.installationWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(formData.installationWarrantyYears)}</p>}
          </div>
        </div>
      </div>

      {/* Picture Upload */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg border-b pb-2">Window Type Picture</h3>
        {/* Show existing picture for edit mode */}
        {isEditing && pictureUrl && (
          <div className="mb-4" key={pictureUrl}>
            <img 
              src={pictureUrl} 
              alt="Window type" 
              className="max-w-full h-auto max-h-64 border rounded"
              onError={(e) => {
                // Hide broken image, show placeholder
                e.target.style.display = 'none';
                const placeholder = e.target.nextSibling;
                if (placeholder) placeholder.style.display = 'block';
              }}
            />
            <div 
              style={{ display: 'none' }} 
              className="p-8 bg-slate-100 border-2 border-dashed rounded text-center text-slate-500"
            >
              <p>Image not found or failed to load</p>
              <p className="text-sm mt-2">Please upload a new picture</p>
            </div>
          </div>
        )}
        {/* Show pending picture preview for add mode */}
        {!isEditing && pendingPicture?.previewUrl && (
          <div className="mb-4">
            <div className="relative inline-block">
              <img 
                src={pendingPicture.previewUrl} 
                alt="Pending window type picture" 
                className="max-w-full h-auto max-h-64 border rounded"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={onRemovePendingPicture}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-green-600 mt-2">✓ Image ready - will be uploaded when window type is created</p>
          </div>
        )}
        <div className="space-y-2">
          <Label>Upload Picture (PNG/JPEG/PDF)</Label>
          <div className="flex gap-2">
            {/* Paste button */}
            <Button
              type="button"
              variant="outline"
              onClick={onPasteFromClipboard}
              title="Paste image from clipboard"
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ClipboardPaste className="h-4 w-4 mr-2" />
              )}
              Paste
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('window-type-picture').click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {(pictureUrl || pendingPicture) ? 'Change Picture' : 'Upload Picture'}
            </Button>
            {pictureUrl && isEditing && onPictureDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={onPictureDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Picture
              </Button>
            )}
            <input
              id="window-type-picture"
              type="file"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              onChange={onPictureUpload}
              style={{ display: 'none' }}
            />
          </div>
          {!isEditing && !pendingPicture && (
            <p className="text-sm text-slate-500">Picture will be uploaded after creating the type</p>
          )}
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={disabled || hasWarrantyErrors([formData.frameWarrantyYears, formData.finishWarrantyYears, formData.hardwareWarrantyYears, formData.installationWarrantyYears])}>
        {disabled ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {isEditing ? 'Updating...' : 'Creating...'}
          </>
        ) : (
          isEditing ? 'Update Window Type' : 'Create Window Type'
        )}
      </Button>
    </form>
  );
};

export default SimpleWindowTypeForm;
